import { Stack, Grid, InputLabel, OutlinedInput, Select, MenuItem, Box, Switch, FormControlLabel } from "@mui/material";
import { FC, useMemo } from "react";
import FormSection from "../Forms/FormSection/form-section";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import { useEffect, useState, useCallback } from "react";
import { assetService, setupService, titleService } from "../../Core/Api/services";
import { IDbBaseMetadataValueInfos } from "../../Core/Api/Types/i-db-base-metadata-value-infos";
import { DbMetadataFieldInfos } from "../../Core/Api/Types/db-metadata-field-infos";
import { CreateNewDbMetadataValue } from "../../Core/Utils/asset-object-utils";
import { MetadataEditable, MetadataInput } from "../MetadataInput/metadata-input-index";
import TableSkeleton from "../Table/TableSkeleton";
import SearchIcon from "@mui/icons-material/Search";
import { MetadataViewType } from "../../Core/Api/Types/metadata-view-type";
import { DbMetadataViewInfos } from "../../Core/Api/Types/db-metadata-view-infos";
import { ObjectType } from "../../Core/Api/Types/object-type";
import { produce } from "immer";
import { BaseMetadataType } from "../../Core/Api/Types/base-metadata-type";
import { CastMetadataInfos } from "../../Core/Api/Types/cast-metadata-infos";
import GlobalConst from "../../Core/Utils/Constants/globals";
import { useTranslation } from "react-i18next";

export type MetadataEditorProps = {
  onMetadataChange?: (
    isValid: boolean,
    metadata: IDbBaseMetadataValueInfos[],
    isAllMandatoryFilled: boolean,
    status: DbMetadataFieldInfos
  ) => void;
  objectIds: number[];
  objectType?: ObjectType;
  viewType?: MetadataViewType;
  byPassMetadataTagNames?: string[];
  isBM?: boolean;
  isCreation?: boolean;
  mandatorySwitch?: boolean;
};

const MetadataEditor: FC<MetadataEditorProps> = (props) => {
  const { t } = useTranslation();
  const {
    onMetadataChange,
    objectIds,
    objectType,
    viewType,
    byPassMetadataTagNames = [],
    isBM = false,
    isCreation = false,
    mandatorySwitch = false,
  } = props;
  const [metadataList, setMetadataList] = useState<DbMetadataFieldInfos[]>([]);
  const [metadataListMandatory, setMetadataListMandatory] = useState<DbMetadataFieldInfos[]>([]);
  const [metadataView, setMetadataView] = useState<DbMetadataViewInfos>();
  const [metadataListFilled, setMetadataListFilled] = useState<IDbBaseMetadataValueInfos[]>([]);
  const [metadataGroups, setMetadataGroups] = useState<string[]>([]);
  const [pageLoaded, setPageLoaded] = useState<boolean>(false);
  const [filter, setFilter] = useState("");
  const [episodesTotalNumber, setEpisodesTotalNumber] = useState<number>();
  const [selectedEpisode, setSelectedEpisode] = useState<number | null>(null);
  const [showOnlyMandatory, setShowOnlyMandatory] = useState<boolean>(false);

  const otherGroupKey = "Other";

  // Main loader
  useEffect(() => {
    if (objectIds.length === 0 && !isCreation) return;
    let request: Promise<any> | null = null;
    if (!isCreation && objectIds.length > 1) {
      request = assetService.GetMetadataFieldsByObjectIds(objectIds);
    } else if (objectIds.length === 1) {
      request = assetService.GetMetadataFieldsByObjectId(objectIds[0]);
    } else if (isCreation) {
      request = titleService.GetMetadataFields();
    }

    if (request) {
      request.then((response) => {
        if (response.success && ((response.values && response.fields) || response.metadataFields)) {
          console.log("asset:GetMetadataFieldsByObjectId(" + objectIds!.join(", ") + ") ok");

          let fields: DbMetadataFieldInfos[] = [];
          let groups: string[] = [];

          response.fields?.forEach((field) => {
            if (Object.values(MetadataEditable).includes(field.baseType?.toString()!)) {
              if (field.group === null) field.group = otherGroupKey;
              fields.push(field);
              if (!groups.includes(field.group)) groups.push(field.group);
            }
          });

          response.metadataFields?.Title?.forEach((field) => {
            if (Object.values(MetadataEditable).includes(field.baseType?.toString())) {
              if (field.group === null) field.group = otherGroupKey;
              fields.push(field);
              if (!groups.includes(field.group)) groups.push(field.group);
            }
          });
          // Move "Other" group to first position
          let indexOther = groups.indexOf(otherGroupKey);

          if (indexOther !== -1) {
            let element = groups[indexOther];
            groups.splice(indexOther, 1);
            groups.splice(0, 0, element);
          }
          const serieSeasonEpisodeTotalNumberMeta = response.fields?.find(
            (x) => x.tagName === "serieSeasonEpisodeTotalNumber"
          );
          if (serieSeasonEpisodeTotalNumberMeta) {
            const value = response.values.find((x) => x.id === serieSeasonEpisodeTotalNumberMeta.id);
            if (value) setEpisodesTotalNumber(value.value as number);
          }

          setMetadataGroups(groups);
          setMetadataList(fields);
          setMetadataListFilled(response.values ?? []);
          setMetadataListMandatory(
            (response.fields ?? response.metadataFields?.Title ?? []).filter((x) => x.isMandatory)
          );

          // if view type specified, fetch view and filter metadata on it
          if (viewType)
            setupService.GetAllMetadataViewsInfo().then((response) => {
              if (response && response.data) {
                let view = objectType
                  ? response.data.find((x) => x.type === viewType && x.objectTypes?.includes(objectType))
                  : response.data.find((x) => x.type === viewType);

                if (view) setMetadataView(view);

                setPageLoaded(true);
              }
            });
          else setPageLoaded(true);
        }
      });
    }
  }, [viewType, objectType, objectIds.length, isCreation]);

  // On metadata field change
  const onChange = useCallback(
    (value: any, id: number, valueIndex?: number) => {
      let meta = metadataList.find((x) => x.id === id);

      if (!meta) return;

      setMetadataListFilled(
        produce((draft) => {
          let index = draft.findIndex((x) => x.id === id);

          if (index === -1) draft.push(CreateNewDbMetadataValue(meta!.id, meta!.baseType!, meta!.tagName!, value));

          index = draft.findIndex((x) => x.id === id);

          const isValueNull = value === null || value === undefined || value === "";
          if (isValueNull) {
            draft[index].value = undefined;
            return;
          }

          switch (meta!.baseType!) {
            case BaseMetadataType.DbStringMetadataValueInfos:
              if (!meta?.values || meta.values.length === 0) draft[index].value = value;
              else draft[index].value = value === null ? null : value.value;
              break;
            case BaseMetadataType.DbAnyObjectMultiMetadataValueInfos:
            case BaseMetadataType.DbStringMultiMetadataValueInfos:
              if (!meta?.values || meta.values.length === 0) draft[index].value = value;
              else draft[index].value = value === null ? null : value.map((v) => v.value);
              break;
            case BaseMetadataType.DbLongMetadataValueInfos:
              if (!meta?.values || meta.values.length === 0) draft[index].value = value;
              else draft[index].value = value === null ? null : parseInt(value.value.toString());
              break;
            case BaseMetadataType.DbDoubleMetadataValueInfos:
              if (!meta?.values || meta.values.length === 0) draft[index].value = value;
              else draft[index].value = value === null ? null : parseFloat(value.value.toString());
              break;
            case BaseMetadataType.DbCastsMetadataValueInfos:
              if (draft[index].value && draft[index].value.length) {
                let count = 0;
                let finalIndex = -1;
                if (draft[index].value && draft[index].value.length) {
                  draft[index].value.forEach((v, i) => {
                    if (v.languageLocalizedCultureId === null && v.epNb === selectedEpisode) {
                      if (count === valueIndex) {
                        finalIndex = i;
                      }
                      count = count + 1;
                    }
                  });
                  if (finalIndex > -1) (draft[index].value as any[])[finalIndex] = value;
                }
              } else draft[index].value = [value];
              break;
            default:
              draft[index].value = value;
              break;
          }
        })
      );
    },
    [metadataListFilled, metadataList]
  );

  const onOrderCastRow = useCallback(
    (targetIndex: number, id: number, valueIndex?: number) => {
      let meta = metadataList.find((x) => x.id === id);

      if (!meta || !valueIndex) return;

      setMetadataListFilled(
        produce((draft) => {
          let index = draft.findIndex((x) => x.id === id);
          if (
            index == -1 ||
            targetIndex < 0 ||
            targetIndex >= draft[index].value.length ||
            valueIndex < 0 ||
            valueIndex >= draft[index].value.length
          )
            return;

          switch (meta!.baseType!) {
            case BaseMetadataType.DbCastsMetadataValueInfos:
              if (draft[index].value && draft[index].value.length) {
                let currentValueIndex = 0;
                if (draft[index].value && draft[index].value.length) {
                  let newArrayOfCast: CastMetadataInfos[] = [];
                  while (currentValueIndex < draft[index].value.length) {
                    if (currentValueIndex == valueIndex) {
                      ++currentValueIndex;
                      continue;
                    } else if (currentValueIndex == targetIndex) {
                      newArrayOfCast.push(draft[index].value[valueIndex] as CastMetadataInfos);
                      newArrayOfCast.push(draft[index].value[currentValueIndex] as CastMetadataInfos);
                      ++currentValueIndex;
                    } else newArrayOfCast.push(draft[index].value[currentValueIndex] as CastMetadataInfos);

                    ++currentValueIndex;
                  }

                  draft[index].value = newArrayOfCast;
                }
              }
              break;
            default:
              console.error("ordering in this metadata is not handle yet.");
              break;
          }
        })
      );
    },
    [metadataListFilled, metadataList]
  );

  const onAddCastRow = () => {
    const meta = metadataList.find((x) => x.baseType === BaseMetadataType.DbCastsMetadataValueInfos);
    const newValue = {
      $type: "VDM.AirLab.Sdk.Core.Types.AssetTypes.CastMetadataInfos, VDM.AirLab.Sdk.Core",
      charName: null,
      epNb: selectedEpisode,
      function: "",
      functionEnum: "",
      languageCode: null,
      languageLocalized: null,
      languageLocalizedCultureId: null,
      lastname: "",
      name: "",
      order: 1,
    };
    setMetadataListFilled(
      produce((draft) => {
        let index = draft.findIndex((x) => x.fieldType === BaseMetadataType.DbCastsMetadataValueInfos);

        if (index === -1) draft.push(CreateNewDbMetadataValue(meta!.id, meta!.baseType!, meta!.tagName!, newValue));

        index = draft.findIndex((x) => x.fieldType === BaseMetadataType.DbCastsMetadataValueInfos);

        if (draft[index].value && draft[index].value.length) draft[index].value.push(newValue);
        else draft[index].value = [newValue];
      })
    );
  };

  const onRemoveCastRow = (index: any) => {
    setMetadataListFilled(
      produce((draft) => {
        let metaIndex = draft.findIndex((x) => x.fieldType === BaseMetadataType.DbCastsMetadataValueInfos);

        if (metaIndex === -1) return;

        let count = 0;
        let finalIndex = -1;
        if (draft[metaIndex].value && draft[metaIndex].value.length) {
          draft[metaIndex].value.forEach((v, i) => {
            if (v.languageLocalizedCultureId === null && v.epNb === selectedEpisode) {
              if (count === index) {
                finalIndex = i;
              }
              count = count + 1;
            }
          });
          const toRemove = draft[metaIndex].value[finalIndex];

          if (finalIndex > -1) {
            (draft[metaIndex].value as any[]).splice(finalIndex, 1);
            const isLastOfCultureAndEpisode =
              draft[metaIndex].value.filter(
                (x, i) =>
                  x?.epNb === toRemove?.epNb && x?.languageLocalizedCultureId === toRemove?.languageLocalizedCultureId
              ).length <= 1;
            if (isLastOfCultureAndEpisode)
              draft[metaIndex].value.push({
                ...toRemove,
                lastname: null,
                name: "delete",
                function: null,
                functionEnum: null,
                order: 9999,
              });
          }
        }
      })
    );
  };
  // On metadata change callback
  useEffect(() => {
    let valid = true;
    let status = metadataList.find((x) => x.tagName == GlobalConst.TITLE_STATUS_TAGNAME);
    let allFilled = true;

    metadataListMandatory.forEach((x) => {
      if (metadataListFilled.find((y) => y.fieldName === x.tagName) !== undefined) {
        let meta = metadataListFilled.find((y) => y.fieldName === x.tagName)?.value;
        if (meta === undefined || meta === null || meta === "") {
          valid = false;
        }
      } else allFilled = false;
    });

    // Trigger external callback
    onMetadataChange && onMetadataChange(valid, metadataListFilled, allFilled, status!);
  }, [metadataListFilled, metadataListMandatory, onMetadataChange]);

  const isGroupContainsFilteredMetadata = useCallback(
    (groupName: string | null) => {
      if (filter === "") return true;

      return (
        metadataList.filter(
          (x) => x.group === groupName && (x.tagName?.includesAllWords(filter) || x.label?.includesAllWords(filter))
        ).length > 0
      );
    },
    [filter, metadataList]
  );

  const viewFilterFieldIds = useMemo(() => {
    if (!metadataView) return null;

    let ids: number[] = [];

    metadataView.groups?.forEach((g) => {
      g.rows?.forEach((r) => {
        r.columns?.forEach((c) => {
          c.metadataFieldIds?.forEach((f) => {
            ids.push(f);
          });
        });
      });
    });

    return ids;
  }, [metadataView]);

  return (
    <>
      {!pageLoaded ? (
        <TableSkeleton />
      ) : (
        <Stack spacing={3}>
          <>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <OutlinedInput
                className="filters-search"
                placeholder={t("field:placeholder.filterMetadata")}
                onChange={(e) => setFilter(e.target.value)}
                sx={{ mb: 2 }}
                fullWidth
                endAdornment={<SearchIcon />}
              />
              {mandatorySwitch && (
                <FormControlLabel
                  sx={{ mt: 1.5, ml: 0 }}
                  control={
                    <Switch
                      checked={showOnlyMandatory}
                      onChange={(e) => setShowOnlyMandatory(e.target.checked)}
                      inputProps={{ "aria-label": "Show only mandatory fields" }}
                    />
                  }
                  label={t("field:label.showOnlyMandatory")}
                />
              )}
            </Stack>

            {metadataGroups
              .filter(
                (x) =>
                  isGroupContainsFilteredMetadata(x) &&
                  (viewFilterFieldIds === null ||
                    metadataList.filter((m) => m.group === x && viewFilterFieldIds.includes(m.id)).length > 0)
              )
              .map((group) => {
                return (
                  <FormSection
                    icon={<MovieOutlinedIcon />}
                    title={group}
                    status="todo"
                    key={group}
                    size="small"
                    defaultExpanded={group === otherGroupKey}
                  >
                    <Grid container spacing={3}>
                      {metadataList
                        .filter((x) => !isBM || x.tagName !== GlobalConst.TITLE_STATUS_TAGNAME) // displayed elsewhere
                        ?.filter(
                          (x) =>
                            x.group === group &&
                            (filter === "" ||
                              x.tagName?.toLowerCase().includes(filter) ||
                              x.label?.toLowerCase().includes(filter)) &&
                            (viewFilterFieldIds === null || viewFilterFieldIds.includes(x.id))
                        )
                        .filter((x) => !byPassMetadataTagNames.includes(x.tagName!))
                        .filter(
                          (x) =>
                            episodesTotalNumber !== null ||
                            x.baseType !== BaseMetadataType.DbStringMultiByEpisodeMetadataValueInfos
                        )
                        .filter((x) => (showOnlyMandatory ? x.isMandatory : true))
                        .map((data: DbMetadataFieldInfos) => (
                          <Grid
                            item
                            md={
                              data.baseType === BaseMetadataType.DbStringMultiByEpisodeMetadataValueInfos ||
                              data.baseType === BaseMetadataType.DbCastsMetadataValueInfos
                                ? 12
                                : 6
                            }
                            key={data.id}
                          >
                            <Box display={"flex"} flexDirection={"row"}>
                              <InputLabel sx={{ mb: "6px" }} htmlFor={data.tagName!}>
                                {data.label}
                              </InputLabel>
                              {episodesTotalNumber && data.baseType === BaseMetadataType.DbCastsMetadataValueInfos && (
                                <Select
                                  sx={{ height: "20px", mb: "6px", ml: 2 }}
                                  value={selectedEpisode ?? undefined}
                                  onChange={(e) => {
                                    setSelectedEpisode(e.target.value ? (e.target.value as number) : null);
                                  }}
                                  label={t("field:label.selectEpisode")}
                                >
                                  <MenuItem key={0} value={0}>
                                    {t("common:global")}
                                  </MenuItem>
                                  {Array.apply(null, Array(episodesTotalNumber)).map((x, i) => (
                                    <MenuItem value={i + 1} key={i + 1}>
                                      {t("common:episode")} {i + 1}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            </Box>
                            <MetadataInput
                              onChange={onChange}
                              data={data}
                              predefinedValues={true}
                              value={metadataListFilled.find((x) => x.id === data.id)}
                              episodesTotalNb={episodesTotalNumber}
                              epNb={selectedEpisode}
                              onOrderCastRow={
                                data.baseType === BaseMetadataType.DbCastsMetadataValueInfos
                                  ? onOrderCastRow
                                  : undefined
                              }
                              onAddRow={
                                data.baseType === BaseMetadataType.DbCastsMetadataValueInfos ? onAddCastRow : undefined
                              }
                              onRemoveRow={
                                data.baseType === BaseMetadataType.DbCastsMetadataValueInfos
                                  ? onRemoveCastRow
                                  : undefined
                              }
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </FormSection>
                );
              })}
          </>
        </Stack>
      )}
    </>
  );
};

export default MetadataEditor;
